import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useWarehouseMutations, useWarehouseTypes } from '~/hooks/use-warehouses';
import { warehouseSchema, EMPTY_WAREHOUSE, type WarehouseFormValues } from '~/lib/schemas/warehouse';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: WarehouseFormValues | null;
}

/** Add / edit a warehouse (Αποθήκη). */
export function WarehouseEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useWarehouseMutations();
	const { data: types } = useWarehouseTypes();
	const form = useForm<WarehouseFormValues>({
		resolver: zodResolver(warehouseSchema) as Resolver<WarehouseFormValues>,
		defaultValues: initial ?? EMPTY_WAREHOUSE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, control, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_WAREHOUSE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('catalog.warehouses.editTitle') : t('catalog.warehouses.addTitle')}</DialogTitle>
					<DialogDescription>{t('catalog.warehouses.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('catalog.warehouses.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('catalog.warehouses.columns.code')} error={formState.errors.cd?.message}>
						<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} />
					</FormField>
					<FormField label={t('catalog.warehouses.columns.type')} error={formState.errors.wt?.message}>
						<Controller
							control={control}
							name="wt"
							render={({ field }) => (
								<Select value={field.value ? String(field.value) : ''} onValueChange={v => field.onChange(Number(v))}>
									<SelectTrigger aria-invalid={Boolean(formState.errors.wt)}>
										<SelectValue placeholder={t('catalog.warehouses.selectType')} />
									</SelectTrigger>
									<SelectContent>
										{(types ?? []).map(ty => (
											<SelectItem key={ty.id} value={String(ty.id)}>
												{ty.nm}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="wh-iv">{t('catalog.warehouses.columns.visible')}</Label>
							<Switch id="wh-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="wh-disabled">{t('catalog.warehouses.columns.disabled')}</Label>
							<Switch
								id="wh-disabled"
								checked={watch('is_disabled')}
								onCheckedChange={next => setValue('is_disabled', next, { shouldDirty: true })}
							/>
						</div>
					</div>
					<FormField label={t('catalog.warehouses.columns.sort')} error={formState.errors.si?.message}>
						<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
